/**
 * Nova saída de material (romaneio) — replicada do sistema antigo.
 *
 * Obra, motorista (cadastrado ou avulso), encarregado, quem autorizou, e os
 * itens. Criar o romaneio NÃO baixa o físico: o material fica comprometido e
 * sai de verdade na retirada (regra de 03/08). O acompanhamento é na aba
 * Romaneios, como sempre.
 *
 * O seletor de item mostra a foto e, ESCOLHIDO o item, busca os três números
 * (físico / comprometido / disponível) — é o disponível que importa aqui, e o
 * campo avisa na hora se a quantidade pedida passa dele.
 */

import { api, ErroApi } from '../api.js';
import { escapar, numero } from '../comum/formato.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';
import { montarBuscaItem, type ItemEscolhido } from '../comum/busca-item.js';

interface Pessoa {
  id: number;
  nome: string;
  funcao: string | null;
  pode_autorizar: boolean;
  modulos: string[];
}

interface LinhaSaida {
  item: ItemEscolhido;
  quantidade: string;
  disponivel: number | null; // null = ainda buscando
}

const AVULSO = '__avulso__';

export function montarNovaSaida(raiz: HTMLElement): void {
  let linhas: LinhaSaida[] = [];

  raiz.innerHTML = `<p class="carregando">Carregando obras e pessoas…</p>`;

  void (async () => {
    let obras: { codigo: string; nome: string }[];
    let pessoas: Pessoa[];
    try {
      const [ro, rp] = await Promise.all([
        api.get<{ obras: { codigo: string; nome: string }[] }>('/api/obras?situacao=ativas'),
        api.get<{ pessoas: Pessoa[] }>('/api/pessoas?status=ativo'),
      ]);
      obras = ro.obras;
      pessoas = rp.pessoas;
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar obras e pessoas.',
      )}</p>`;
      return;
    }
    desenhar(obras, pessoas);
  })();

  function desenhar(obras: { codigo: string; nome: string }[], pessoas: Pessoa[]): void {
    const opcaoPessoa = (p: Pessoa) => `<option value="${escapar(p.nome)}">${escapar(p.nome)}</option>`;
    const motoristas = pessoas.filter((p) => p.modulos.includes('motorista'));
    const autorizadores = pessoas.filter((p) => p.pode_autorizar);

    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>🚚 Nova saída de material (romaneio)</h3>
        <div class="form-2col">
          <label class="rotulado"><span>Obra destino</span>
            <select id="ns-obra">
              <option value="">— escolha —</option>
              ${obras.map((o) => `<option value="${escapar(o.codigo)}">${escapar(o.nome)}</option>`).join('')}
            </select></label>
          <label class="rotulado"><span>Motorista</span>
            <select id="ns-motorista">
              <option value="">— escolha —</option>
              ${motoristas.map(opcaoPessoa).join('')}
              <option value="${AVULSO}">✏️ Outro (sem cadastro)</option>
            </select>
            <input id="ns-motorista-avulso" hidden placeholder="Nome do motorista sem cadastro" maxlength="120"></label>
          <label class="rotulado"><span>Encarregado que solicitou</span>
            <select id="ns-encarregado">
              <option value="">— escolha —</option>
              ${pessoas.map(opcaoPessoa).join('')}
            </select></label>
          <label class="rotulado"><span>Quem autorizou a saída</span>
            <select id="ns-autorizou">
              <option value="">— escolha —</option>
              ${autorizadores.map(opcaoPessoa).join('')}
            </select></label>
        </div>
        <label class="rotulado"><span>Observações (opcional)</span>
          <input id="ns-obs" maxlength="1000"></label>

        <h3>Itens da saída</h3>
        <div id="ns-linhas"></div>
        <div id="ns-busca"></div>

        <p class="dica">Criar o romaneio ainda não baixa o estoque — os itens ficam
          comprometidos e saem de verdade na retirada pelo motorista.</p>
        <div class="conf-acoes">
          <button class="btn btn-grande" id="ns-criar">✓ Criar romaneio</button>
        </div>
        <p class="aviso" id="ns-erro" hidden></p>
      </section>`;

    const caixaLinhas = raiz.querySelector<HTMLElement>('#ns-linhas')!;
    const erro = raiz.querySelector<HTMLElement>('#ns-erro')!;
    const selMotorista = raiz.querySelector<HTMLSelectElement>('#ns-motorista')!;
    const avulso = raiz.querySelector<HTMLInputElement>('#ns-motorista-avulso')!;

    selMotorista.addEventListener('change', () => {
      avulso.hidden = selMotorista.value !== AVULSO;
      if (!avulso.hidden) avulso.focus();
    });

    montarBuscaItem(raiz.querySelector<HTMLElement>('#ns-busca')!, (item) => {
      linhas.push({ item, quantidade: '', disponivel: null });
      desenharLinhas();
      caixaLinhas.querySelector<HTMLInputElement>('.ne-linha:last-child [data-campo="quantidade"]')?.focus();
      // Os três números chegam por trás — a linha já está na tela.
      void api
        .get<{ disponivel: number }>(`/api/estoque/${encodeURIComponent(item.codigo)}`)
        .then((e) => {
          const l = linhas.find((x) => x.item.codigo === item.codigo && x.disponivel === null);
          if (!l) return;
          l.disponivel = e.disponivel;
          desenharLinhas();
        })
        .catch(() => {});
    });

    function desenharLinhas(): void {
      if (linhas.length === 0) {
        caixaLinhas.innerHTML = `<p class="dica">Busque um item abaixo — escolher já adiciona a linha.</p>`;
        return;
      }
      caixaLinhas.innerHTML = linhas
        .map((l, i) => {
          const estoura = l.disponivel !== null && Number(l.quantidade) > l.disponivel;
          return `
        <div class="ne-linha" data-i="${i}">
          <span class="ped-cfoto">${
            l.item.miniatura
              ? `<img src="${l.item.miniatura}" alt="" ${l.item.foto ? `data-grande="${escapar(api.foto(l.item.foto) ?? '')}" data-fotos-de="${escapar(l.item.codigo)}"` : ''}>`
              : '<span class="sem">—</span>'
          }</span>
          <span class="ped-cnome">
            <b>${escapar(l.item.nome)}</b>
            <span class="sub-inline">${escapar(l.item.codigo)} · disponível: ${
              l.disponivel === null ? '…' : `${numero(l.disponivel)} ${escapar(l.item.unidade ?? '')}`
            }</span>
            ${estoura ? `<span class="ped-alerta">pede mais do que o disponível</span>` : ''}
          </span>
          <label class="rotulado ne-c"><span>Qtd.</span>
            <input type="number" min="0.001" step="any" data-campo="quantidade" value="${escapar(l.quantidade)}"></label>
          <button class="ped-tirar" data-tirar="${i}" title="Tirar da saída">✕</button>
        </div>`;
        })
        .join('');
    }

    caixaLinhas.addEventListener('change', (e) => {
      const campo = e.target as HTMLInputElement;
      const linha = campo.closest<HTMLElement>('[data-i]');
      if (!linha || campo.dataset['campo'] !== 'quantidade') return;
      linhas[Number(linha.dataset['i'])]!.quantidade = campo.value;
      desenharLinhas(); // reavalia o aviso de disponível
    });
    caixaLinhas.addEventListener('input', (e) => {
      const campo = e.target as HTMLInputElement;
      const linha = campo.closest<HTMLElement>('[data-i]');
      if (!linha || campo.dataset['campo'] !== 'quantidade') return;
      linhas[Number(linha.dataset['i'])]!.quantidade = campo.value;
    });
    caixaLinhas.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-tirar]');
      if (!b) return;
      linhas.splice(Number(b.dataset['tirar']), 1);
      desenharLinhas();
    });

    raiz.querySelector('#ns-criar')!.addEventListener('click', () => void criar());

    async function criar(): Promise<void> {
      erro.hidden = true;
      const obra = raiz.querySelector<HTMLSelectElement>('#ns-obra')!.value;
      const motorista =
        selMotorista.value === AVULSO ? avulso.value.trim() : selMotorista.value;
      const encarregado = raiz.querySelector<HTMLSelectElement>('#ns-encarregado')!.value;
      const autorizou = raiz.querySelector<HTMLSelectElement>('#ns-autorizou')!.value;

      // As mesmas exigências do sistema antigo.
      if (!obra || !motorista || !encarregado || !autorizou) {
        avisarBloqueante(
          'Preencha obra, motorista, encarregado e quem autorizou.' +
            (selMotorista.value === AVULSO && !motorista
              ? ' (digite o nome do motorista sem cadastro)'
              : ''),
        );
        return;
      }
      if (linhas.length === 0) {
        avisarBloqueante('Adicione ao menos um item.');
        return;
      }
      const semQtd = linhas.find((l) => !(Number(l.quantidade) > 0));
      if (semQtd) {
        avisarBloqueante(`"${semQtd.item.nome}" está sem quantidade.`);
        return;
      }

      const botao = raiz.querySelector<HTMLButtonElement>('#ns-criar')!;
      botao.disabled = true;
      botao.textContent = 'Criando…';
      try {
        const r = await api.post<{ numero: string }>('/api/romaneios', {
          obra,
          motorista,
          encarregado,
          autorizou,
          itens: linhas.map((l) => ({
            codigo: l.item.codigo,
            quantidade: Number(l.quantidade),
          })),
          ...(raiz.querySelector<HTMLInputElement>('#ns-obs')!.value.trim()
            ? { obs: raiz.querySelector<HTMLInputElement>('#ns-obs')!.value.trim() }
            : {}),
        });
        linhas = [];
        desenharLinhas();
        raiz.querySelector<HTMLInputElement>('#ns-obs')!.value = '';
        avisar(`Romaneio ${r.numero} criado — acompanhe na aba Romaneios.`);
      } catch (e) {
        if (e instanceof ErroApi) {
          erro.innerHTML = `${escapar(e.message)}${
            e.detalhes?.length ? `<br>· ${e.detalhes.map(escapar).join('<br>· ')}` : ''
          }`;
        } else {
          erro.textContent = 'Não consegui criar o romaneio. Tente de novo.';
        }
        erro.hidden = false;
      } finally {
        botao.disabled = false;
        botao.textContent = '✓ Criar romaneio';
      }
    }

    desenharLinhas();
  }
}
